<script setup lang="ts">
import {computed, type DeepReadonly, onMounted, ref} from 'vue'
import {requireInject} from '@/common/utils/InjectUtil'
import {PRODUCT_SELECTION_INJECTION} from '@/common/services/Products/productSelectionInjection'
import {$inj} from '@/common/decorators/depinject'
import {type AccountLoanRequestedAmount} from '@/common/services/LoanPurposes/loanRequestedAmountFactory'
import {VsgCurrencyInput} from '@jack-henry/jha-vue-components'
import {type ProductLoanDetail, ProductLoanDetailService} from '@/common/services/Products/productLoanDetailService'
import type IAccount from '@/common/models/IAccount'
import FormatFactory from '@/common/services/utils/FormatFactory'

const props = defineProps<{
  account: DeepReadonly<IAccount>
}>()

const injection = requireInject(PRODUCT_SELECTION_INJECTION)
const productLoanDetailService = $inj(ProductLoanDetailService)
const formatFactory = $inj(FormatFactory)
const loanDetail = ref<ProductLoanDetail>()

onMounted(() => {
  productLoanDetailService.getAll(props.account.accountTypeId, {cache: true}).then((response) => {
    loanDetail.value = response.data.payload.length
      ? response.data.payload[0]
      : {accountTypeId: props.account.accountTypeId}
  })
})

const accountLoanRequestedAmount = computed<AccountLoanRequestedAmount>(
  () =>
    injection.values.accountRequestedAmounts.value.get(props.account.accountId) ?? {
      accountId: props.account.accountId
    }
)

const updateRequestedLoanAmount = (event: FocusEvent) => {
  return injection.methods.updateAccountRequestedAmount({
    ...accountLoanRequestedAmount.value,
    amount: formatStringToNumber((event.target as HTMLInputElement).value)
  })
}

const formatStringToNumber = (string: string) => parseFloat(string.replaceAll(',', ''))

const isRequired = computed(() => loanDetail.value?.requestedAmountMaximum || loanDetail.value?.requestedAmountMinimum)

const minCurrency = computed(() =>
  loanDetail.value?.requestedAmountMinimum ? formatFactory.currency(loanDetail.value?.requestedAmountMinimum) : null
)
const maxCurrency = computed(() =>
  loanDetail.value?.requestedAmountMaximum ? formatFactory.currency(loanDetail.value?.requestedAmountMaximum) : null
)
const assistiveText = computed(() => {
  let string = ``;
  if (minCurrency.value) {
    string += `min. ${minCurrency.value}`
  }

  if (minCurrency.value && maxCurrency.value) {
    string += ` / `
  }

  if (maxCurrency.value) {
    string += `max. ${maxCurrency.value}`
  }

  return string
})

</script>
<template>
  <vsg-currency-input
    label="Requested loan amount"
    data-cy="requested-loan-amount-input"
    v-if="loanDetail?.requestedAmountVisible"
    :required="isRequired"
    :min="loanDetail?.requestedAmountMinimum"
    :max="loanDetail?.requestedAmountMaximum"
    outline
    :assistiveText
    @blur="updateRequestedLoanAmount"
    :value="accountLoanRequestedAmount.amount" />
</template>

<style scoped>
jha-form-currency-input {
  --error-text-top-margin: 0;
}
</style>
